// DropZone — drag-and-drop + click-to-browse video intake.
// Pure presentational; bubbles selected files up via onFiles.

import React, { useCallback, useRef, useState } from 'react';
import { UploadCloud } from 'lucide-react';

interface DropZoneProps {
  onFiles: (files: FileList | File[]) => void;
  disabled?: boolean;
  compact?: boolean;
}

export default function DropZone({ onFiles, disabled, compact }: DropZoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      if (e.dataTransfer?.files?.length) onFiles(e.dataTransfer.files);
    },
    [onFiles, disabled],
  );

  const handlePick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) onFiles(e.target.files);
      e.target.value = ''; // allow re-picking the same file
    },
    [onFiles],
  );

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={[
        'group relative flex flex-col items-center justify-center gap-2 rounded-xl',
        'border border-dashed text-center transition-colors cursor-pointer select-none',
        compact ? 'px-4 py-4' : 'px-6 py-10',
        disabled
          ? 'opacity-50 cursor-not-allowed border-white/10'
          : dragOver
            ? 'border-neon_purple-400/70 bg-neon_purple-400/[0.06]'
            : 'border-white/15 hover:border-white/30 bg-white/[0.02]',
      ].join(' ')}
    >
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        multiple
        hidden
        onChange={handlePick}
      />
      <UploadCloud
        className={[
          compact ? 'h-5 w-5' : 'h-8 w-8',
          dragOver ? 'text-neon_purple-300' : 'text-neutral-400 group-hover:text-neutral-200',
        ].join(' ')}
      />
      <div className="space-y-0.5">
        <p className={`font-medium text-white/90 ${compact ? 'text-xs' : 'text-sm'}`}>
          {dragOver ? 'Drop to add clips' : 'Drag & drop videos here'}
        </p>
        {!compact && (
          <p className="text-[11px] text-neutral-500">
            or click to browse · MP4, MOV, WebM, MKV · processed entirely in your browser
          </p>
        )}
      </div>
    </div>
  );
}
